import React, { Suspense } from 'react';
import { Timeline } from './ui/timeline';
import { products } from '@/lib/data';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { Button } from './ui/neon-button';
import { useTranslation } from 'react-i18next';
import { Product } from '@/lib/types';
import { LoadingSpinner } from './LoadingSpinner';
import { ImageWithFallback } from './ImageWithFallback';

const Products = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const currentLanguage = i18n.language.split('-')[0] as keyof Product['translations'];

  const timelineData = products.map(product => ({
    title: product.translations[currentLanguage].title,
    content: (
      <motion.div
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        onClick={() => navigate(`/product/${product.id}`)}
        className="cursor-pointer group"
      >
        <p className="text-[#555555] text-xs md:text-sm font-normal mb-8">
          {product.translations[currentLanguage].description}
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="relative overflow-hidden rounded-lg">
            <Suspense fallback={<LoadingSpinner />}>
              <ImageWithFallback
                src={product.images.main}
                alt={product.translations[currentLanguage].title}
                className="rounded-lg object-cover h-60 w-full shadow-lg transform group-hover:scale-105 transition-transform duration-300"
                loading="lazy"
              />
            </Suspense>
            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-300" />
          </div>
          <div className="space-y-4">
            <div className="space-y-2">
              {product.translations[currentLanguage].features.map((feature, index) => (
                <div key={index} className="flex gap-2 items-center text-[#555555] text-xs md:text-sm">
                  ✓ {feature}
                </div>
              ))}
            </div>
            <div className="flex items-center justify-between mt-4">
              <span className="text-[#1E3A8A] font-semibold">
                {t('products.currency')}{product.price.toLocaleString()}
              </span>
              <span className={`px-3 py-1 rounded-full text-xs ${
                product.availability === 'In Stock' 
                  ? 'bg-green-100 text-green-800'
                  : 'bg-blue-100 text-blue-800'
              }`}>
                {t(`products.${product.availability === 'In Stock' ? 'inStock' : 'preOrder'}`)}
              </span>
            </div>
            <Button 
              variant="solid" 
              size="default"
              className="w-full flex items-center justify-center gap-2"
            >
              {t('products.viewDetails')}
              <ArrowRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </motion.div>
    ),
  }));

  return (
    <section id="products" className="bg-white">
      <Timeline data={timelineData} />
    </section>
  );
};

export default Products;